import ModalLayout from "@/components/layout/ModalLayout";
import Table, { TableColumn } from "@/components/ui/Table";
import { Button } from "@/components/ui/Button";
import ModalHeader from "@/components/ui/ModalHeader";
import { useState, useEffect } from "react";
import { useUserInfo } from "@/hooks/member/useMember";
import { useMyMenuList, useMyMenuSave } from "@/hooks/menu/useMenu";
import { MenuWithFavorite, MyMenuResponse } from "@/interface";

interface MenuItem {
  id: number;
  parentMenu: string;
  subMenu: string;
}

interface MyMenuModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function MyMenuModal({ isOpen, onClose }: MyMenuModalProps) {
  const { data: userInfo } = useUserInfo();
  const { data: myMenuResponse } = useMyMenuList(userInfo?.name || "") as { data: MyMenuResponse | undefined };
  const { mutate: saveMyMenu } = useMyMenuSave();

  // 전체 메뉴 데이터 (left)
  const [allMenus, setAllMenus] = useState<MenuItem[]>([]);

  // 마이 메뉴 데이터 (right)
  const [myMenus, setMyMenus] = useState<MenuItem[]>([]);

  // myMenuResponse 변경 시 데이터 초기화
  useEffect(() => {
    if (myMenuResponse) {
      const convertToMenuItem = (menu: MenuWithFavorite): MenuItem => ({
        id: menu.menuId,
        parentMenu: menu.parentName || "",
        subMenu: menu.name || "",
      });

      // left 배열을 전체메뉴로
      const leftMenus: MenuItem[] = myMenuResponse.left.map(convertToMenuItem);

      // right 배열을 마이메뉴로
      const rightMenus: MenuItem[] = myMenuResponse.right.map(convertToMenuItem);

      setAllMenus(leftMenus);
      setMyMenus(rightMenus);
    }
  }, [myMenuResponse]);

  // 전체 메뉴 선택된 행 인덱스
  const [selectedAllMenus, setSelectedAllMenus] = useState<number[]>([]);

  // 마이 메뉴 선택된 행 인덱스
  const [selectedMyMenus, setSelectedMyMenus] = useState<number[]>([]);

  // >> 버튼: 전체 메뉴에서 마이 메뉴로 이동
  const handleMoveToMyMenu = () => {
    const itemsToMove = selectedAllMenus.map(index => allMenus[index]);
    setMyMenus(prev => [...prev, ...itemsToMove]);
    setAllMenus(prev => prev.filter((_, index) => !selectedAllMenus.includes(index)));
    setSelectedAllMenus([]);
  };

  // << 버튼: 마이 메뉴에서 전체 메뉴로 이동
  const handleMoveToAllMenu = () => {
    const itemsToMove = selectedMyMenus.map(index => myMenus[index]);
    setAllMenus(prev => [...prev, ...itemsToMove]);
    setMyMenus(prev => prev.filter((_, index) => !selectedMyMenus.includes(index)));
    setSelectedMyMenus([]);
  };

  // 테이블 컬럼 정의
  const columns: TableColumn[] = [
    { key: "parentMenu", header: "상위 메뉴", headerAlign: "left", bodyAlign: "left" },
    { key: "subMenu", header: "하위 메뉴", headerAlign: "left", bodyAlign: "left" },
  ];

  const handleSave = () => {
    if (!userInfo?.name) {
      console.error("사용자 정보를 찾을 수 없습니다.");
      return;
    }

    // 오른쪽 테이블(myMenus)에 있는 모든 메뉴 ID 추출
    const favoriteMenuIds = myMenus.map(menu => menu.id);

    // 저장할 데이터 형식
    const saveData = {
      memberName: userInfo.name,
      favoriteMenuIds: favoriteMenuIds,
    };

    // API 호출
    saveMyMenu(saveData, {
      onSuccess: () => {
        onClose();
      },
    });
  };

  return (
    <ModalLayout
      isOpen={isOpen}
      onClose={onClose}
      title="마이메뉴 관리"
      maxWidth="max-w-6xl"
      zIndexClass="z-[9999]"
      usePortal
    >
      <div className="flex flex-col h-full">
        {/* 두 테이블 사이의 버튼 영역 */}
        <div className="flex gap-5 mb-4">
          {/* 전체 메뉴 테이블 */}
          <div className="flex-1 flex flex-col">
            <ModalHeader title="전체메뉴" />
            <div className="border border-[#EBEBEB] rounded overflow-hidden">
              <Table
                columns={columns}
                data={allMenus}
                showCheckbox
                showRowNumber
                selectedRows={selectedAllMenus}
                onRowSelect={setSelectedAllMenus}
                maxHeight="calc(70vh - 200px)"
              />
            </div>
          </div>

          {/* 중간 버튼 영역 */}
          <div className="flex flex-col justify-center gap-2">
            <div
              onClick={handleMoveToMyMenu}
              className={`p-2 border border-[#CCCCCC] rounded cursor-pointer flex items-center justify-center ${
                selectedAllMenus.length === 0 ? "opacity-50 pointer-events-none" : "hover:bg-gray-50"
              }`}
              style={{ borderWidth: "1px", borderRadius: "4px" }}
            >
              <img src="/icons/doubleArrowRight.svg" className="w-5 h-5" />
            </div>
            <div
              onClick={handleMoveToAllMenu}
              className={`p-2 border border-[#CCCCCC] rounded cursor-pointer flex items-center justify-center ${
                selectedMyMenus.length === 0 ? "opacity-50 pointer-events-none" : "hover:bg-gray-50"
              }`}
              style={{ borderWidth: "1px", borderRadius: "4px" }}
            >
              <img src="/icons/doubleArrowLeft.svg" className="w-5 h-5" />
            </div>
          </div>

          {/* 마이 메뉴 테이블 */}
          <div className="flex-1 flex flex-col">
            <ModalHeader title="마이메뉴" />
            <div className="border border-[#EBEBEB] rounded overflow-hidden">
              <Table
                columns={columns}
                data={myMenus}
                showCheckbox
                showRowNumber
                selectedRows={selectedMyMenus}
                onRowSelect={setSelectedMyMenus}
                maxHeight="calc(70vh - 200px)"
              />
            </div>
          </div>
        </div>

        {/* 하단 버튼 */}
        <div className="flex justify-between mt-4">
          <Button variant="white" onClick={onClose} className="min-w-[80px]">
            취소
          </Button>
          <Button variant="primary" onClick={handleSave} className="min-w-[80px]">
            저장
          </Button>
        </div>
      </div>
    </ModalLayout>
  );
}
